import { formatAddress, formatName } from '@utils/format';

import type {
  ContactPoint,
  PatientModel,
  PractitionerModel,
  RelatedPersonModel,
} from 'generated/types';

import { FhirResource } from './fhir-resource';

export type ContactType = PatientModel | PractitionerModel | RelatedPersonModel;
export type PatientContactType = 'care-team' | 'related-person';

export class Contact extends FhirResource {
  public model: ContactType;

  public constructor(model: ContactType) {
    super(model);
    this.model = model;
  }

  public get id() {
    return this.model.id;
  }

  public get isActive() {
    return this.model.active;
  }

  public get primaryAddress() {
    const [primary] = this.model.address ?? [];
    if (!primary) {
      return null;
    }
    const { city, country, line, postalCode, state } = primary;
    return formatAddress(line, city, postalCode, state, country);
  }

  public get secondaryAddress() {
    const secondary = this.model.address?.[1];
    if (!secondary) {
      return null;
    }
    const { city, country, line, postalCode, state } = secondary;
    return formatAddress(line, city, postalCode, state, country);
  }

  // Primary address fields
  public get address() {
    const [primary] = this.model.address ?? [];
    return primary?.line?.[0];
  }

  public set address(value: string | null | undefined) {
    const secondaryAddress = this.model.address?.[1] ?? null;
    this.model = {
      ...this.model,
      address: [
        {
          ...this.model.address?.[0],
          line: value ? [value].concat(this.address2 ?? []) : null,
        },
        ...(secondaryAddress ? [secondaryAddress] : []),
      ],
    };
  }

  public get address2() {
    const [primary] = this.model.address ?? [];
    return primary?.line?.[1];
  }

  public set address2(value: string | null | undefined) {
    const secondaryAddress = this.model.address?.[1] ?? null;
    this.model = {
      ...this.model,
      address: [
        {
          ...this.model.address?.[0],
          line: value
            ? [this.address ?? ' '].concat(value)
            : this.address
              ? [this.address]
              : null,
        },
        ...(secondaryAddress ? [secondaryAddress] : []),
      ],
    };
  }

  public get postalCode() {
    const [primary] = this.model.address ?? [];
    return primary?.postalCode;
  }

  public set postalCode(value: string | null | undefined) {
    const secondaryAddress = this.model.address?.[1] ?? null;
    this.model = {
      ...this.model,
      address: [
        {
          ...this.model.address?.[0],
          postalCode: value ?? null,
        },
        ...(secondaryAddress ? [secondaryAddress] : []),
      ],
    };
  }

  public get city() {
    const [primary] = this.model.address ?? [];
    return primary?.city;
  }

  public set city(value: string | null | undefined) {
    const secondaryAddress = this.model.address?.[1] ?? null;
    this.model = {
      ...this.model,
      address: [
        {
          ...this.model.address?.[0],
          city: value ?? null,
        },
        ...(secondaryAddress ? [secondaryAddress] : []),
      ],
    };
  }

  public get region() {
    const [primary] = this.model.address ?? [];
    return primary?.state;
  }

  public set region(value: string | null | undefined) {
    const secondaryAddress = this.model.address?.[1] ?? null;
    this.model = {
      ...this.model,
      address: [
        {
          ...this.model.address?.[0],
          state: value ?? null,
        },
        ...(secondaryAddress ? [secondaryAddress] : []),
      ],
    };
  }

  public get country() {
    const [primary] = this.model.address ?? [];
    return primary?.country;
  }

  public set country(value: string | null | undefined) {
    const secondaryAddress = this.model.address?.[1] ?? null;
    this.model = {
      ...this.model,
      address: [
        {
          ...this.model.address?.[0],
          country: value ?? null,
        },
        ...(secondaryAddress ? [secondaryAddress] : []),
      ],
    };
  }

  // Secondary address fields
  public get secondAddress() {
    const secondary = this.model.address?.[1];
    return secondary?.line?.[0];
  }

  public set secondAddress(value: string | null | undefined) {
    const primaryAddress = this.model.address?.[0] ?? { line: ['-'] };
    this.model = {
      ...this.model,
      address: [
        primaryAddress,
        {
          ...this.model.address?.[1],
          line: value ? [value].concat(this.secondAddressUnit ?? []) : null,
        },
      ],
    };
  }

  public get secondAddressUnit() {
    const secondary = this.model.address?.[1];
    return secondary?.line?.[1];
  }

  public set secondAddressUnit(value: string | null | undefined) {
    const primaryAddress = this.model.address?.[0] ?? { line: ['-'] };

    this.model = {
      ...this.model,
      address: [
        primaryAddress,
        {
          ...this.model.address?.[1],
          line: value
            ? [this.secondAddress ?? ' '].concat(value)
            : this.secondAddress
              ? [this.secondAddress]
              : null,
        },
      ],
    };
  }

  public get secondPostalCode() {
    const secondary = this.model.address?.[1];
    return secondary?.postalCode;
  }

  public set secondPostalCode(value: string | null | undefined) {
    this.model = {
      ...this.model,
      address: [
        this.model.address?.[0] ?? { line: ['-'] },
        {
          ...this.model.address?.[1],
          postalCode: value ?? null,
        },
      ],
    };
  }

  public get secondCity() {
    const secondary = this.model.address?.[1];
    return secondary?.city;
  }

  public set secondCity(value: string | null | undefined) {
    this.model = {
      ...this.model,
      address: [
        this.model.address?.[0] ?? { line: ['-'] },
        {
          ...this.model.address?.[1],
          city: value ?? null,
        },
      ],
    };
  }

  public get secondRegion() {
    const secondary = this.model.address?.[1];
    return secondary?.state;
  }

  public set secondRegion(value: string | null | undefined) {
    this.model = {
      ...this.model,
      address: [
        this.model.address?.[0] ?? { line: ['-'] },
        {
          ...this.model.address?.[1],
          state: value ?? null,
        },
      ],
    };
  }

  public get secondCountry() {
    const secondary = this.model.address?.[1];
    return secondary?.country;
  }

  public set secondCountry(value: string | null | undefined) {
    this.model = {
      ...this.model,
      address: [
        this.model.address?.[0] ?? { line: ['-'] },
        {
          ...this.model.address?.[1],
          country: value ?? null,
        },
      ],
    };
  }

  // Name fields
  public get name() {
    const primary = this.model.name?.at(0);
    if (!primary) {
      return null;
    }
    const { family, given, prefix, suffix } = primary;
    return formatName(
      prefix?.join(' '),
      given?.join(' '),
      family,
      suffix?.join(' ')
    );
  }

  public get first_name() {
    const primary = this.model.name?.at(0);
    if (!primary) {
      return null;
    }
    const { given } = primary;
    return given?.join(' ');
  }

  public set first_name(value: string | null | undefined) {
    this.model = {
      ...this.model,
      name: value
        ? [
            {
              ...this.model.name?.[0],
              given: [value],
            },
          ]
        : [],
    };
  }

  public get last_name() {
    const primary = this.model.name?.at(0);
    if (!primary) {
      return null;
    }
    const { family } = primary;
    return family;
  }

  public set last_name(value: string | null | undefined) {
    this.model = {
      ...this.model,
      name: value
        ? [
            {
              ...this.model.name?.[0],
              family: value,
            },
          ]
        : [],
    };
  }

  public get category() {
    if (Contact.isPractitioner(this.model)) {
      return 'PRACTITIONER TYPE';
    } else if (Contact.isRelatedPerson(this.model)) {
      return 'PERSON TYPE';
    } else if (Contact.isPatient(this.model)) {
      return 'PATIENT TYPE';
    }
    return null;
  }

  public get phone() {
    const contact = this.model.telecom?.filter((a) => a?.system === 'phone');
    const [phone] = contact ?? [];
    if (!phone?.value) {
      return null;
    }
    return phone.value;
  }

  public set phone(value: string | null | undefined) {
    const contact = this.model.telecom?.filter((a) => a?.system !== 'phone');

    this.model = {
      ...this.model,
      telecom: [
        ...(contact ?? []),
        ...(value ? [{ system: 'phone', value }] : []),
      ] as ContactPoint[],
    };
  }

  public get email() {
    const contact = this.model.telecom?.filter((a) => a?.system === 'email');
    const [email] = contact ?? [];
    return email?.value;
  }

  public set email(value: string | null | undefined) {
    const contact = this.model.telecom?.filter((a) => a?.system !== 'email');

    this.model = {
      ...this.model,
      telecom: [
        ...(contact ?? []),
        ...(value ? [{ system: 'email', value }] : []),
      ] as ContactPoint[],
    };
  }

  public static isPractitioner(
    model: Contact | ContactType
  ): model is PractitionerModel {
    if (model instanceof Contact) {
      return model.model.__typename === 'PractitionerModel';
    }
    return model.__typename === 'PractitionerModel';
  }

  public static isRelatedPerson(
    model: Contact | ContactType
  ): model is RelatedPersonModel {
    if (model instanceof Contact) {
      return model.model.__typename === 'RelatedPersonModel';
    }
    return model.__typename === 'RelatedPersonModel';
  }

  public static isPatient(model: Contact | ContactType): model is PatientModel {
    if (model instanceof Contact) {
      return model.model.__typename === 'PatientModel';
    }
    return model.__typename === 'PatientModel';
  }
}
